/**
 * Strings da feature de assinatura, registradas no namespace único `translation`
 * do portal sob a chave `subscription` (ver `@/lib/i18n`). Os componentes usam
 * `useTranslation(undefined, { keyPrefix: "subscription" })`.
 */
export const subscriptionPtBR = {
  tabLabel: "Faturas",
  section: {
    title: "Assinatura e pagamentos",
    subtitle:
      "Acompanhe o status da sua assinatura, próxima cobrança e histórico de pagamentos.",
    pastDueBanner: "Há fatura(s) em atraso. Regularize para evitar a suspensão da conta.",
    cancelledBanner:
      "Sua assinatura foi cancelada. O histórico de pagamentos continua disponível abaixo.",
    expiredBanner:
      "Seu período de teste expirou. Para voltar a usar todos os recursos, reative a assinatura.",
  },
  statusCard: {
    title: "Status da assinatura",
    subtitle: "Resumo do plano vigente e da próxima cobrança.",
    planPrefix: "Plano",
    nextInvoice: "Próxima cobrança",
    dueOn: "vence em {{date}}",
    defaultMethod: "Forma de pagamento:",
    none: {
      title: "Você ainda não escolheu um plano",
      description: "Escolha um plano para ativar todos os recursos do ImobGo.",
      cta: "Escolher um plano",
    },
  },
  status: {
    none: "Sem plano",
    active: "Ativa",
    trialing: "Em teste",
    past_due: "Em atraso",
    cancelled: "Cancelada",
    expired: "Expirada",
  },
  origin: {
    free_trial: {
      title: "Período de teste",
      description: "Você está experimentando o ImobGo gratuitamente.",
      endsAt: "Termina em {{date}}",
    },
    free_premium: {
      title: "Premium cortesia",
      description: "Acesso premium concedido pela equipe ImobGo.",
    },
    courtesy: {
      title: "Cortesia comercial",
      description: "Cortesia concedida pela equipe ImobGo.",
    },
    paid: {
      title: "Assinatura paga",
      description: "Cobranças recorrentes pelo método cadastrado.",
    },
  },
  billingCycle: { monthly: "mês", annual: "ano" },
  method: { credit_card: "Cartão de crédito", pix: "PIX", boleto: "Boleto" },
  paymentStatus: {
    paid: "Pago",
    scheduled: "A vencer",
    overdue: "Vencido",
    pending: "Aguardando",
    failed: "Falhou",
    refunded: "Estornado",
    chargeback: "Contestado",
  },
  history: {
    title: "Histórico de pagamentos",
    subtitle: "Faturas geradas e seu status.",
    filterLabel: "Filtrar histórico",
    filter: { all: "Tudo", scheduled: "A vencer", overdue: "Vencidos", paid: "Pagos", refunded: "Estornados" },
    competence: "Competência",
    amount: "Valor",
    dueDate: "Vencimento",
    paidAt: "Pago em",
    overdueHint: "Em atraso",
    pagination: { summary: "Mostrando {{from}}–{{to}} de {{total}}", prev: "Anterior", next: "Próxima" },
    empty: {
      all: {
        title: "Nenhum pagamento registrado",
        subtitle: "Sua assinatura está em período cortesia ou ainda não houve cobrança.",
      },
      scheduled: {
        title: "Nenhuma fatura a vencer",
        subtitle: "Quando uma nova cobrança for gerada, ela aparece aqui.",
      },
      overdue: { title: "Nenhuma fatura em atraso", subtitle: "Tudo em dia por aqui." },
      paid: { title: "Nenhum pagamento confirmado", subtitle: "Você ainda não tem cobranças pagas." },
      refunded: { title: "Nenhum estorno", subtitle: "Não há pagamentos estornados ou contestados." },
    },
  },
  actions: {
    receipt: "Recibo",
    support: "Falar com suporte",
    copyBarcode: "Copiar código",
    pdf: "Baixar PDF",
    copyPix: "Copiar PIX",
    barcodeCopied: "Código de barras copiado.",
    pixCopied: "PIX copiado.",
    copyError: "Não foi possível copiar. Tente novamente.",
  },
} as const;

export const subscriptionEnUS = {
  tabLabel: "Billing",
  section: {
    title: "Subscription & payments",
    subtitle: "Track your subscription status, next charge, and payment history.",
    pastDueBanner: "You have overdue invoice(s). Settle them to avoid account suspension.",
    cancelledBanner: "Your subscription was cancelled. Payment history remains available below.",
    expiredBanner: "Your trial has expired. Reactivate your subscription to keep using all features.",
  },
  statusCard: {
    title: "Subscription status",
    subtitle: "Summary of your current plan and next charge.",
    planPrefix: "Plan",
    nextInvoice: "Next charge",
    dueOn: "due on {{date}}",
    defaultMethod: "Payment method:",
    none: {
      title: "You haven't chosen a plan yet",
      description: "Pick a plan to unlock all of ImobGo's features.",
      cta: "Choose a plan",
    },
  },
  status: {
    none: "No plan",
    active: "Active",
    trialing: "Trial",
    past_due: "Past due",
    cancelled: "Cancelled",
    expired: "Expired",
  },
  origin: {
    free_trial: { title: "Trial period", description: "You're trying ImobGo for free.", endsAt: "Ends on {{date}}" },
    free_premium: { title: "Complimentary premium", description: "Premium access granted by the ImobGo team." },
    courtesy: { title: "Commercial courtesy", description: "Courtesy granted by the ImobGo team." },
    paid: { title: "Paid subscription", description: "Recurring charges to your saved payment method." },
  },
  billingCycle: { monthly: "month", annual: "year" },
  method: { credit_card: "Credit card", pix: "PIX", boleto: "Boleto" },
  paymentStatus: {
    paid: "Paid",
    scheduled: "Scheduled",
    overdue: "Overdue",
    pending: "Pending",
    failed: "Failed",
    refunded: "Refunded",
    chargeback: "Chargeback",
  },
  history: {
    title: "Payment history",
    subtitle: "Invoices and their current status.",
    filterLabel: "Filter history",
    filter: { all: "All", scheduled: "Scheduled", overdue: "Overdue", paid: "Paid", refunded: "Refunded" },
    competence: "Period",
    amount: "Amount",
    dueDate: "Due date",
    paidAt: "Paid on",
    overdueHint: "Overdue",
    pagination: { summary: "Showing {{from}}–{{to}} of {{total}}", prev: "Previous", next: "Next" },
    empty: {
      all: {
        title: "No payments yet",
        subtitle: "Your subscription may be in a courtesy period or no charge has been generated yet.",
      },
      scheduled: { title: "No upcoming invoices", subtitle: "New charges will appear here as soon as they're generated." },
      overdue: { title: "No overdue invoices", subtitle: "Everything is up to date." },
      paid: { title: "No confirmed payments", subtitle: "You don't have any settled invoices yet." },
      refunded: { title: "No refunds", subtitle: "There are no refunded or charged-back payments." },
    },
  },
  actions: {
    receipt: "Receipt",
    support: "Contact support",
    copyBarcode: "Copy barcode",
    pdf: "Download PDF",
    copyPix: "Copy PIX",
    barcodeCopied: "Barcode copied.",
    pixCopied: "PIX copied.",
    copyError: "Couldn't copy. Try again.",
  },
} as const;
